import type { ReadingOptions, SearchableSet, SearchContext } from '@gymcloud/set-search'

import type { DistanceUnits, LoggedSet, Units, Workout } from '~/data/types'
import type { Enrollment } from '~/data/templates'
import { firstDayOfWeek } from '~/lib/weekdays'

/**
 * A search of training history as this browser reads it — `/specs/features/search.spec.md`. A
 * moment is read on the device's own clock and calendar, and a date in the order the device
 * writes one, so those are asked of the browser here rather than assumed.
 */

/** Whether this browser writes a date with the day before the month. */
export function dayFirstHere(): boolean {
  try {
    const order = new Intl.DateTimeFormat(navigator.language)
      .formatToParts(new Date(2024, 11, 25))
      .map((part) => part.type)
      .filter((type) => type === 'day' || type === 'month')
    return order[0] === 'day'
  } catch {
    return true
  }
}

export function readingHere(): ReadingOptions {
  return { dayFirst: dayFirstHere() }
}

export function contextHere(units: Units, distanceUnits: DistanceUnits = 'km'): SearchContext {
  return {
    units,
    distanceUnits,
    now: Date.now(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    firstDayOfWeek: firstDayOfWeek(),
  }
}

/**
 * A set as a search asks of it: its own names and numbers, and what it was part of — the
 * workout, and the template that workout was played from, off what the device holds of both.
 */
export function searchableOf(
  set: LoggedSet,
  workouts: ReadonlyMap<string, Workout>,
  runs: ReadonlyMap<string, Enrollment>,
): SearchableSet {
  const workout = set.workoutId === null ? undefined : workouts.get(set.workoutId)
  const run = workout?.enrollmentId == null ? undefined : runs.get(workout.enrollmentId)
  return {
    exerciseName: set.exerciseName,
    notes: set.notes,
    weightKg: set.weightKg,
    reps: set.reps,
    durationSeconds: set.durationSeconds,
    distanceMeters: set.distanceMeters,
    rpe: set.rpe,
    performedAt: set.performedAt,
    songs: set.songs ?? [],
    workoutName: workout?.name ?? null,
    workoutNotes: [workout?.openingNotes ?? null, workout?.closingNotes ?? null],
    templateName: run?.templateName ?? null,
    templateWorkout: workout?.templateWorkout ?? null,
  }
}
